import React, { ChangeEvent, DragEvent, useRef, useState } from 'react';

import { ReactComponent as UploadFileIcon } from '../../assets/images/uploadFile.svg';
import admanToast from '../Alert/util/admanToast';
import { VALID_FORMATS } from './constants';
import { DragAndDropFileAreaWrapper, FileRequirementsWrapper, HeaderWrapper } from './styled';
import { validateFile } from './utils';

export type Props = {
  handleStartUploadFile: (file: File) => void;
  className?: string;
};

const DragAndDropFileArea = ({ handleStartUploadFile, className }: Props): JSX.Element => {
  const [isOver, setIsOver] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsOver(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsOver(false);

    if (event.dataTransfer.files.length > 1) {
      admanToast.error('Only one selected video file should be dropped here');
      return;
    }

    const droppedFile: File = Array.from(event.dataTransfer.files)[0];

    handleUploadWithValidation(droppedFile);
  };

  const handleClickUploadFile = (): void => {
    inputRef.current?.click();
  };

  const handleLoadFile = (event: ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = event.target.files?.[0];

    if (!handleUploadWithValidation(selectedFile)) {
      event.target.value = '';
    }
  };

  const handleUploadWithValidation = (file?: File): boolean => {
    if (file && validateFile(file)) {
      handleStartUploadFile(file);
      return true;
    }

    return false;
  };

  return (
    <DragAndDropFileAreaWrapper
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      $isOver={isOver}
      className={className}
      data-testid="dragAndDropFileAreaWrapper"
    >
      <div>
        <HeaderWrapper data-testid="dragAndDropFileAreaHeaderWrapper">
          Drop a video file here or{' '}
          <span onClick={handleClickUploadFile} data-testid="dragAndDropFileAreaUploadFile">
            click to upload
          </span>
        </HeaderWrapper>
        <div>
          <UploadFileIcon data-testid="dragAndDropFileAreaUploadIcon" />
        </div>
        <FileRequirementsWrapper data-testid="dragAndDropFileRequirementsWrapper">
          <div data-testid="dragAndDropFileRequirementsFormat">Format: .mp4 or .mov</div>
          <div data-testid="dragAndDropFileRequirementsSize">Max File Size: 3 GB</div>
          <div data-testid="dragAndDropFileRequirementsDuration">Duration: 15–30 Seconds</div>
        </FileRequirementsWrapper>
      </div>
      <input
        data-testid="dragAndDropFileField"
        ref={inputRef}
        type="file"
        onChange={handleLoadFile}
        accept={VALID_FORMATS.join(', ')}
        hidden
      />
    </DragAndDropFileAreaWrapper>
  );
};

export default DragAndDropFileArea;
